import axios from "axios";
import { BASE_URL, HEADER, API_ENDPOINTS, STORAGE_KEYS } from "../constants";
import {
  isAuthEndpoint,
  applyHeaders,
  isSpecialFlowEndpoint,
} from "../utils/auth";
import { getSessionStorage, setSessionStorage } from "../utils/storageUtils";

const client = axios.create({
  baseURL: BASE_URL,
});

let isRefreshing = false;
let failedQueue = [];
let onApiError = null;

export const setGlobalErrorHandler = (handler) => {
  onApiError = handler;
};

const handleLogout = () => {
  const storePath = getSessionStorage(STORAGE_KEYS.CUSTOM_PATH);
  window.location.replace(`/app/${storePath}`);
  sessionStorage.clear();
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// =======================
// Refresh token
// =======================
export const refreshTokens = async () => {
  const response = await client.post(API_ENDPOINTS.REFRESH_TOKEN, null);
  const newAccessToken = response.headers[HEADER.AUTHORIZATION];
  const newRefreshToken = response.headers[HEADER.REFRESH_TOKEN];

  setSessionStorage(HEADER.AUTHORIZATION, newAccessToken);
  setSessionStorage(HEADER.REFRESH_TOKEN, newRefreshToken);

  return { accessToken: newAccessToken };
};

client.interceptors.response.use(
  (response) => {
    const { headers } = response;

    if (headers[HEADER.FLOW_ID]) {
      setSessionStorage(HEADER.FLOW_ID, headers[HEADER.FLOW_ID]);
    }
    if (headers[HEADER.NAFATH_CODE]) {
      setSessionStorage(HEADER.NAFATH_CODE, headers[HEADER.NAFATH_CODE]);
    }

    if (headers[HEADER.AUTHORIZATION] && headers[HEADER.REFRESH_TOKEN]) {
      setSessionStorage(HEADER.AUTHORIZATION, headers[HEADER.AUTHORIZATION]);
      setSessionStorage(HEADER.REFRESH_TOKEN, headers[HEADER.REFRESH_TOKEN]);
    }

    if (response.config.url === API_ENDPOINTS.REFRESH_TOKEN) {
      return response;
    }

    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // ✅ If it was canceled by something else (navigation/unmount), keep as-is
    if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    if (!isSpecialFlowEndpoint(originalRequest)) {
      const requestId =
        error?.response?.headers?.[HEADER.FLOW_ID] ||
        error?.response?.data?.request_id;

      if (onApiError) {
        onApiError({
          requestId,
          requestConfig: originalRequest,
        });
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      if (
        originalRequest?.url === API_ENDPOINTS.REFRESH_TOKEN &&
        error.response?.status === 401
      ) {
        if (onApiError) {
          onApiError({
            type: "SESSION_EXPIRED",
          });
        }
        return Promise.reject(error);
      }
      if (isAuthEndpoint(originalRequest)) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) =>
          failedQueue.push({ resolve, reject })
        )
          .then((token) => {
            originalRequest.headers[HEADER.AUTHORIZATION] = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const { accessToken } = await refreshTokens();
        processQueue(null, accessToken);

        originalRequest.headers[HEADER.AUTHORIZATION] = `Bearer ${accessToken}`;
        return client(originalRequest);
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

client.interceptors.request.use(
  (config) => {
    return applyHeaders(config);
  },
  (error) => Promise.reject(error)
);

export default client;
